// client/src/components/returns/ReturnModal.tsx

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Loader2,
  Search,
  AlertCircle,
  ShoppingCart,
  Package,
  DollarSign,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { validateSaleForReturn, processReturn } from "@/lib/api/";
import { getCurrentUser, getCurrentShop, isOwnerInShopMode } from "@/lib/auth";
import { formatCurrency } from "@/lib/currency";
import { SaleSearch } from "./SaleSearch";

interface ReturnModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  initialSaleId?: string;
}

export function ReturnModal({
  open,
  onOpenChange,
  onSuccess,
  initialSaleId,
}: ReturnModalProps) {
  const queryClient = useQueryClient();
  const currentUser = getCurrentUser();
  const currentShop = getCurrentShop();

  const [saleId, setSaleId] = useState(initialSaleId || "");
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Map<string, number>>(
    new Map(),
  );
  const [refundMethod, setRefundMethod] = useState<
    "cash" | "card" | "transfer" | "original"
  >("original");
  const [notes, setNotes] = useState("");
  const [conditions, setConditions] = useState<
    Map<string, "good" | "damaged" | "expired">
  >(new Map());
  const [showSaleSearch, setShowSaleSearch] = useState(false);

  useEffect(() => {
    if (open) {
      if (initialSaleId) {
        setSaleId(initialSaleId);
        handleSearch();
      } else {
        setSaleId("");
        setSearchPerformed(false);
        setSelectedItems(new Map());
        setConditions(new Map());
        setRefundMethod("original");
        setNotes("");
      }
    }
  }, [open, initialSaleId]);

  const {
    data: validationData,
    isLoading: isValidating,
    refetch: validateSale,
    error: validationError,
  } = useQuery({
    queryKey: ["validate-return", saleId],
    queryFn: () => validateSaleForReturn(saleId),
    enabled: false,
  });

  const processMutation = useMutation({
    mutationFn: async () => {
      // 1. Validar datos básicos
      if (!currentShop || !currentUser) {
        throw new Error("No hay información de tienda o usuario");
      }

      // 2. Obtener userId VÁLIDO para processedBy
      let processedById: string | undefined;

      if (isOwnerInShopMode()) {
        // ✅ EN MODO OWNER: Usar el ownerId real de la tienda (SIEMPRE es UUID)
        processedById = currentShop.ownerId;
        console.log(
          "👑 [ReturnModal] Usando ownerId de tienda:",
          processedById,
        );
      } else {
        // Usuario normal: usar su ID
        processedById = currentUser.id;
      }

      // 3. Validar que processedById sea un UUID válido (formato estándar)
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

      if (!processedById || !uuidRegex.test(processedById)) {
        console.error("❌ [ReturnModal] processedBy inválido:", {
          valor: processedById,
          esOwnerMode: isOwnerInShopMode(),
          currentUser: currentUser,
          currentShop: currentShop,
        });
        throw new Error(
          "No se pudo identificar al usuario. Por favor, recarga la página e intenta de nuevo.",
        );
      }

      // 4. Construir items para la devolución
      const items = Array.from(selectedItems.entries())
        .filter(([_, quantity]) => quantity > 0)
        .map(([saleItemId, quantity]) => {
          const availableItem = validationData?.availableItems.find(
            (i) => i.saleItemId === saleItemId,
          );
          return {
            saleItemId,
            productId: availableItem?.productId || "",
            quantity,
            condition: conditions.get(saleItemId) || "good",
          };
        });

      // 5. Enviar al backend
      return processReturn({
        shopId: currentShop.id,
        originalSaleId: saleId,
        items,
        refundMethod,
        // Si es owner, enviar undefined (será NULL en BD)
        processedBy: isOwnerInShopMode() ? undefined : processedById,
        shiftId: undefined,
        notes: notes || undefined,
      });
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: "✅ Devolución procesada",
          description: `Devolución ${data.returnRecord?.returnNumber} completada exitosamente`,
        });

        queryClient.invalidateQueries({ queryKey: ["shop-returns"] });
        queryClient.invalidateQueries({ queryKey: ["shop-inventory"] });
        queryClient.invalidateQueries({ queryKey: ["shop-sales"] });

        onOpenChange(false);
        if (onSuccess) onSuccess();
      } else {
        toast({
          title: "❌ Error",
          description: data.error || "Error al procesar la devolución",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "❌ Error",
        description: error.message || "Error al procesar la devolución",
        variant: "destructive",
      });
    },
  });

  const handleSearch = async () => {
    if (!saleId.trim()) {
      toast({
        title: "Error",
        description: "Ingresa un ID de venta",
        variant: "destructive",
      });
      return;
    }

    setSearchPerformed(true);
    await validateSale();
  };

  const handleItemSelect = (saleItemId: string, maxQuantity: number) => {
    const newSelected = new Map(selectedItems);
    if (newSelected.has(saleItemId)) {
      newSelected.delete(saleItemId);
      const newConditions = new Map(conditions);
      newConditions.delete(saleItemId);
      setConditions(newConditions);
    } else {
      newSelected.set(saleItemId, 1);
    }
    setSelectedItems(newSelected);
  };

  const handleQuantityChange = (
    saleItemId: string,
    quantity: number,
    maxQuantity: number,
  ) => {
    const newQuantity = Math.min(Math.max(1, quantity), maxQuantity);
    const newSelected = new Map(selectedItems);
    newSelected.set(saleItemId, newQuantity);
    setSelectedItems(newSelected);
  };

  const handleConditionChange = (
    saleItemId: string,
    condition: "good" | "damaged" | "expired",
  ) => {
    const newConditions = new Map(conditions);
    newConditions.set(saleItemId, condition);
    setConditions(newConditions);
  };

  const calculateTotal = (): number => {
    if (!validationData?.availableItems) return 0;

    let total = 0;
    for (const [saleItemId, quantity] of selectedItems.entries()) {
      const item = validationData.availableItems.find(
        (i) => i.saleItemId === saleItemId,
      );
      if (item) {
        total += item.unitPrice * quantity;
      }
    }
    return total;
  };

  const isValidForSubmission = (): boolean => {
    if (selectedItems.size === 0) return false;

    for (const [id, qty] of selectedItems.entries()) {
      if (qty <= 0) return false;
    }

    for (const id of selectedItems.keys()) {
      if (!conditions.has(id)) {
        setConditions((prev) => new Map(prev).set(id, "good"));
      }
    }

    return true;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Procesar Devolución</DialogTitle>
        </DialogHeader>

        {/* Paso 1: Buscar venta */}
        {!validationData && !isValidating && (
          <div className="space-y-4 py-2 sm:py-4">
            <div className="space-y-2">
              <Label>Seleccionar Venta</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  placeholder="ID de la venta"
                  value={saleId}
                  onChange={(e) => setSaleId(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={handleSearch}
                  disabled={!saleId.trim()}
                  className="w-full sm:w-auto"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Validar
                </Button>
              </div>
              <div className="relative my-3">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    o
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowSaleSearch(true)}
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Buscar venta en historial
              </Button>
            </div>

            {searchPerformed && validationError && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                  <p className="text-destructive font-medium">
                    Error al validar la venta
                  </p>
                </div>
                <p className="text-sm text-destructive/80 mt-1">
                  {(validationError as any)?.message ||
                    "No se pudo validar la venta"}
                </p>
              </div>
            )}
          </div>
        )}

        {isValidating && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="ml-2 text-muted-foreground">Validando venta...</p>
          </div>
        )}

        {/* Paso 2: Mostrar items disponibles para devolver */}
        {validationData && (
          <div className="space-y-6 py-2 sm:py-4">
            {!validationData.isValid && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <p className="text-amber-800 font-medium">
                    No se puede procesar la devolución
                  </p>
                </div>
                <ul className="list-disc list-inside text-sm text-amber-700 mt-2">
                  {validationData.errors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {validationData.isValid && (
              <>
                {/* Tabla de items - Desktop */}
                <div className="hidden md:block">
                  <h3 className="font-medium mb-3">
                    Selecciona los items a devolver
                  </h3>
                  <div className="border rounded-lg overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">Sel.</TableHead>
                          <TableHead>Producto</TableHead>
                          <TableHead className="text-right">
                            Precio Unit.
                          </TableHead>
                          <TableHead className="text-center">
                            Disponible
                          </TableHead>
                          <TableHead className="text-center">
                            Cantidad
                          </TableHead>
                          <TableHead>Condición</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {validationData.availableItems.map((item) => (
                          <TableRow key={item.saleItemId}>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={selectedItems.has(item.saleItemId)}
                                onCheckedChange={() =>
                                  handleItemSelect(
                                    item.saleItemId,
                                    item.maxReturnable,
                                  )
                                }
                                disabled={item.maxReturnable === 0}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              {item.productName}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(item.unitPrice, "CUP")}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge
                                variant={
                                  item.maxReturnable > 0
                                    ? "outline"
                                    : "secondary"
                                }
                              >
                                {item.maxReturnable}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {selectedItems.has(item.saleItemId) && (
                                <Input
                                  type="number"
                                  min="1"
                                  max={item.maxReturnable}
                                  value={
                                    selectedItems.get(item.saleItemId) || 1
                                  }
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      item.saleItemId,
                                      parseInt(e.target.value) || 1,
                                      item.maxReturnable,
                                    )
                                  }
                                  className="w-20 mx-auto text-center"
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {selectedItems.has(item.saleItemId) && (
                                <Select
                                  value={
                                    conditions.get(item.saleItemId) || "good"
                                  }
                                  onValueChange={(v: any) =>
                                    handleConditionChange(item.saleItemId, v)
                                  }
                                >
                                  <SelectTrigger className="w-28">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="good">Bueno</SelectItem>
                                    <SelectItem value="damaged">
                                      Dañado
                                    </SelectItem>
                                    <SelectItem value="expired">
                                      Vencido
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Tarjetas de items - Móvil */}
                <div className="md:hidden">
                  <h3 className="font-medium mb-3">
                    Selecciona los items a devolver
                  </h3>
                  <div className="space-y-3">
                    {validationData.availableItems.map((item) => (
                      <Card key={item.saleItemId} className="overflow-hidden">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <p className="font-medium">{item.productName}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatCurrency(item.unitPrice, "CUP")} c/u
                              </p>
                            </div>
                            <Checkbox
                              checked={selectedItems.has(item.saleItemId)}
                              onCheckedChange={() =>
                                handleItemSelect(
                                  item.saleItemId,
                                  item.maxReturnable,
                                )
                              }
                              disabled={item.maxReturnable === 0}
                            />
                          </div>

                          {selectedItems.has(item.saleItemId) && (
                            <div className="mt-3 pt-3 border-t space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm">Disponible:</span>
                                <Badge variant="outline">
                                  {item.maxReturnable}
                                </Badge>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm">Cantidad:</span>
                                <Input
                                  type="number"
                                  min="1"
                                  max={item.maxReturnable}
                                  value={
                                    selectedItems.get(item.saleItemId) || 1
                                  }
                                  onChange={(e) =>
                                    handleQuantityChange(
                                      item.saleItemId,
                                      parseInt(e.target.value) || 1,
                                      item.maxReturnable,
                                    )
                                  }
                                  className="w-24 text-center"
                                />
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-sm">Condición:</span>
                                <Select
                                  value={
                                    conditions.get(item.saleItemId) || "good"
                                  }
                                  onValueChange={(v: any) =>
                                    handleConditionChange(item.saleItemId, v)
                                  }
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="good">Bueno</SelectItem>
                                    <SelectItem value="damaged">
                                      Dañado
                                    </SelectItem>
                                    <SelectItem value="expired">
                                      Vencido
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Método de reembolso */}
                <div className="space-y-2">
                  <Label htmlFor="refundMethod">Método de Reembolso</Label>
                  <Select
                    value={refundMethod}
                    onValueChange={(v: any) => setRefundMethod(v)}
                  >
                    <SelectTrigger id="refundMethod">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="original">
                        Mismo método original
                      </SelectItem>
                      <SelectItem value="cash">Efectivo</SelectItem>
                      <SelectItem value="card">Tarjeta</SelectItem>
                      <SelectItem value="transfer">Transferencia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notas */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notas (opcional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Motivo de la devolución, observaciones, etc."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                  />
                </div>

                {/* Resumen */}
                <div className="bg-muted/30 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Total a reembolsar:</span>
                    <span className="text-2xl font-bold text-primary">
                      {formatCurrency(calculateTotal(), "CUP")}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Package className="h-3 w-3" />
                      {selectedItems.size} item
                      {selectedItems.size !== 1 ? "s" : ""}
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3" />
                      {Array.from(selectedItems.values()).reduce(
                        (a, b) => a + b,
                        0,
                      )}{" "}
                      unidad
                      {Array.from(selectedItems.values()).reduce(
                        (a, b) => a + b,
                        0,
                      ) !== 1
                        ? "es"
                        : ""}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Cancelar
          </Button>
          {validationData?.isValid && (
            <Button
              onClick={() => processMutation.mutate()}
              disabled={processMutation.isPending || !isValidForSubmission()}
              className="w-full sm:w-auto order-1 sm:order-2"
            >
              {processMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                "Procesar Devolución"
              )}
            </Button>
          )}
        </DialogFooter>

        <SaleSearch
          open={showSaleSearch}
          onOpenChange={setShowSaleSearch}
          onSelectSale={(selectedSaleId) => {
            setSaleId(selectedSaleId);
            setSearchPerformed(false);
            setTimeout(() => {
              validateSale();
            }, 100);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
